/**
 * 设置分区的子页定义。
 *
 * `state: 'planned'` 的条目**不是**"以后再说"，而是这个分区当前最要紧的诚实声明：
 * 每一页都写清"会提供什么、为什么现在是空的、由哪个任务填充"。
 * 用假数据把页面填满会让"接线到底通没通"变得无法判断 —— 而那正是本阶段要验证的唯一一件事。
 */

export type SetupTab =
  | 'summary'
  | 'params'
  | 'app'
  | 'airframe'
  | 'radio'
  | 'safety'
  | 'power'
  | 'servo'
  | 'tuning'
  | 'calibration';

export interface SetupTabPlan {
  /** 这一页将来会提供什么（具体到用户能做什么） */
  readonly willProvide: string;
  /** 为什么现在是空的（缺的是哪一块能力） */
  readonly missingBecause: string;
  /** 由哪个后续任务填充（todo 名或包名） */
  readonly owner: string;
}

export interface SetupTabDefinition {
  readonly id: SetupTab;
  readonly label: string;
  readonly hint: string;
  readonly state: 'ready' | 'planned';
  readonly plan: SetupTabPlan | null;
}

export const SETUP_TABS: readonly SetupTabDefinition[] = Object.freeze([
  {
    id: 'summary',
    label: '机体摘要',
    hint: '机型、固件、参数与传感器健康，以及还没配好的项',
    state: 'ready',
    plan: null,
  },
  {
    id: 'params',
    label: '参数编辑器',
    hint: '全量参数：搜索、分组、改值、写入并回读确认',
    state: 'ready',
    plan: null,
  },
  {
    id: 'app',
    label: '应用设置',
    hint: '外观、单位、上次链路与本地数据',
    state: 'ready',
    plan: null,
  },
  {
    id: 'airframe',
    label: '机架类型',
    hint: '机架类别与布局、固件版本、SERIAL 端口',
    state: 'planned',
    plan: {
      willProvide:
        '选择机架类别与布局（FRAME_CLASS / FRAME_TYPE）、查看固件版本、配置串口与协议（SERIALn_*）。' +
        '这一页的价值是"解锁之前把机架告诉飞控"，它决定电机与舵机的输出顺序。',
      missingBecause:
        '它需要三样目前还没有的能力：固件版本上报（AUTOPILOT_VERSION 目前没有被车辆状态机收下）、' +
        '按机架类型校验参数组合的领域知识、以及写参数后强制重启飞控的流程。' +
        '现在只能改参数，改对了也没有任何东西能确认"机架组合是自洽的"。',
      owner: 'setup-airframe',
    },
  },
  {
    id: 'radio',
    label: '遥控器',
    hint: '遥控通道映射、校准行程、失控保护',
    state: 'planned',
    plan: {
      willProvide:
        '逐通道校准遥控行程（RCMAP_* / RCn_MIN / RCn_TRIM / RCn_MAX / RCn_REVERSED），' +
        '并把失控保护（FS_THR_*）与当前遥控器状态一起调到可用。',
      missingBecause:
        '校准要求"读通道原始 PWM → 提示拨杆 → 写回校准值 → 再回读确认"这个闭环，' +
        '而遥控通道读数（RC_CHANNELS）虽然进来了，却还没有任何东西把"用户此刻拨到哪"与参数写入串起来。' +
        '用当前读数画一张表会让人以为已经校准过了。',
      owner: 'setup-radio',
    },
  },
  {
    id: 'safety',
    label: '安全',
    hint: '解锁检查、失控保护、地理围栏与返航',
    state: 'planned',
    plan: {
      willProvide:
        '把 ARMING_* 解锁检查、FS_* 失控保护、FENCE_* 地理围栏、RTL_* 返航这四组参数放在一处，' +
        '并给出"这一组是否自洽"的判断（例如围栏开着但半径是 0、失控保护动作设成了无）。',
      missingBecause:
        '判断"自洽"需要一组按机型与飞行场景定义的规则（现在没有任何地方定义它们），' +
        '而这些参数在参数编辑器里可以逐条改但没有组合校验 —— 分散显示一套"看起来像检查"的列表会更危险。',
      owner: 'setup-safety',
    },
  },
  {
    id: 'power',
    label: '电源与电机',
    hint: '电池监控标定、电机顺序与怠速',
    state: 'planned',
    plan: {
      willProvide:
        '配置电池监控（BATT_MONITOR / BATT_CAPACITY / 分压比）、电量与电压告警阈值，' +
        '以及电机顺序检查（逐个电机点动）与怠速（MOT_SPIN_ARM / MOT_SPIN_MIN）。',
      missingBecause:
        '电机点动是**会驱动螺旋桨**的动作，必须有"已上锁 + 桨已拆 + 逐电机确认"这套流程与提示，' +
        '还要有命令侧的支持（DO_MOTOR_TEST 已经具备，但流程与安全门禁还没有设计）。' +
        '在没有这套流程前给出按钮，等于把"误转螺旋桨"变成一个可以随便点的操作。',
      owner: 'setup-power-motors',
    },
  },
  {
    id: 'servo',
    label: '舵机与云台',
    hint: '输出通道功能、舵机行程、云台控制',
    state: 'planned',
    plan: {
      willProvide:
        '给每个输出通道指定功能（SERVOn_FUNCTION）、设置行程与中位（SERVOn_MIN / TRIM / MAX / REVERSED），' +
        '并配置云台（MNT1_*）的机型与控制方式。',
      missingBecause:
        '改 SERVOn_FUNCTION 会立刻改变某个物理输出在做什么（改错可能让舵机顶到极限），' +
        '需要一个"先看当前接线、再改、改完要求重启并复检"的向导；这部分依赖机架页的固件与机型信息。',
      owner: 'setup-servo-camera',
    },
  },
  {
    id: 'tuning',
    label: '调参',
    hint: '姿态增益、自动调参、振动与频谱',
    state: 'planned',
    plan: {
      willProvide:
        '姿态控制器增益（ATC_*）的调整向导、自动调参（AUTOTUNE_*）的启动与观测、' +
        '以及振动（VIBRATION / FFT）的谱图分析。',
      missingBecause:
        '调参的本质是"改一点 → 试飞 → 看响应曲线"，而试飞数据的记录与曲线分析属于遥测回放（analyze）那一块，' +
        '目前尚未接入。单独给一张增益表，用户只能盲调。',
      owner: 'setup-tuning',
    },
  },
  {
    id: 'calibration',
    label: '传感器校准',
    hint: '加速度计、罗盘、水平与磁力计状态',
    state: 'planned',
    plan: {
      willProvide:
        '加速度计六面校准、罗盘校准与磁力计健康评估、水平校准，全部带进度与失败原因，' +
        '校准结果与时间会被记录下来（"最后一次校准"才有答案）。',
      missingBecause:
        '校准管理器（@hgc/core 的 calibration）正在并发开发：它负责指令时序、进度播报与失败判定。' +
        '在它接进来之前，这一页无法真正开始校准，也无法伪造一条"已完成"。' +
        '更关键的是：没有校准记录存储，"最后一次校准时间"就是不可知的 —— 这也是本页如实说"没有记录"的原因。',
      owner: 'setup-sensors / core-calibration',
    },
  },
]);

export const DEFAULT_SETUP_TAB: SetupTab = 'summary';

export function setupTabOf(id: SetupTab): SetupTabDefinition {
  const found = SETUP_TABS.find((tab) => tab.id === id);
  // 类型上不可能发生；真发生时退回摘要页而不是抛错（界面不该因为一个页签名崩掉）
  return found ?? SETUP_TABS[0]!;
}

export function isSetupTab(value: string): value is SetupTab {
  return SETUP_TABS.some((tab) => tab.id === value);
}
